<template>
    <div
        class="Checkbox"
        :class="[
            {
                'Checkbox--hasError': hasError,
                'Checkbox--disabled': disabled,
                'Checkbox--indeterminate': indeterminate,
            },
            `Checkbox--${size}`,
            variant ? `Checkbox--${variant}` : '',
        ]"
    >
        <label :for="computedId" class="Checkbox-label">
            <input
                ref="checkbox"
                type="checkbox"
                :id="computedId"
                :name="name"
                :disabled="disabled"
                :checked="value"
                :indeterminate.prop="indeterminate"
                :required="required"
                :data-testid="testId"
                :aria-checked="indeterminate ? 'mixed' : value.toString()"
                :aria-describedby="hasError ? `${computedId}-error` : undefined"
                @change="handleChange"
                @keydown="handleKeydown"
                class="Checkbox-input"
            />
            <div class="Checkbox-indicator">
                <i v-if="indeterminate" class="ri-subtract-line"></i>
                <i v-else-if="value" class="ri-check-line"></i>
            </div>
            <div class="Checkbox-content" v-if="$slots.default">
                <slot />
            </div>
        </label>
    </div>
</template>

<script>
export default {
    props: {
        size: {
            type: String,
            default: 'medium',
        },
        variant: {
            type: String,
            default: '',
            validator: (value) => ['', 'white'].includes(value),
        },
        name: String,
        hasError: Boolean,
        disabled: {
            type: Boolean,
            default: false,
        },
        indeterminate: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Boolean,
            default: false,
        },
        testId: {
            type: String,
            default: null,
        },
    },
    computed: {
        computedId() {
            return this.name || `checkbox-${this._uid}`;
        },
    },
    methods: {
        handleChange(event) {
            if (this.disabled) return;

            const isChecked = event.target.checked;

            if (this.indeterminate) {
                this.$refs.checkbox.indeterminate = false;
            }

            this.$emit('input', isChecked);
            this.$emit('change', isChecked);
        },

        handleKeydown(event) {
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                this.$refs.checkbox.click();
            }
        },

        setIndeterminate(state) {
            this.$refs.checkbox.indeterminate = state;
        },
    },
};
</script>

<style lang="scss" scoped>
.Checkbox {
    display: inline-flex;
    align-items: center;
    position: relative;
    margin: 4px 0;

    &-input {
        position: absolute;
        opacity: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        cursor: pointer;
        z-index: 1;
        margin: 0;

        &:focus + .Checkbox-indicator {
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
        }
    }

    &-label {
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
    }

    &-indicator {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        background-color: white;
        border: 0.5px solid var(--color-gray, #95a5a6);
        border-radius: 4px;
        transition:
            background-color 0.2s ease,
            border-color 0.2s ease;
        position: relative;
        flex-shrink: 0;

        i {
            color: white;
            font-size: 16px;
            line-height: 1;
            position: absolute;
        }
    }

    &-content {
        font-size: 14px;
        line-height: 1.5;
        color: var(--color-text, #333);
        flex: 1;
    }

    &-input:checked + .Checkbox-indicator {
        background-color: var(--color-blue-dark);
        border-color: var(--color-blue-dark);
    }

    &--indeterminate .Checkbox-indicator {
        background-color: var(--color-blue-dark);
        border-color: var(--color-blue-dark);
    }

    &--indeterminate &-input:checked + .Checkbox-indicator {
        background-color: var(--color-blue-dark);
        border-color: var(--color-blue-dark);
    }

    &--disabled {
        opacity: 0.6;

        .Checkbox-label {
            cursor: not-allowed;
        }

        .Checkbox-indicator {
            background-color: #f5f5f5;
        }
    }

    &--hasError .Checkbox-indicator {
        border-color: var(--color-red, #e74c3c);
    }

    &--white {
        .Checkbox-indicator {
            background-color: transparent;
            border-color: rgba(255, 255, 255, 0.3);
        }

        .Checkbox-input:checked + .Checkbox-indicator {
            background-color: rgba(255, 255, 255, 0.8);
            border-color: rgba(255, 255, 255, 0.8);
        }

        .Checkbox-input:checked + .Checkbox-indicator i {
            color: var(--color-blue-dark);
        }

        &.Checkbox--indeterminate .Checkbox-indicator {
            background-color: rgba(255, 255, 255, 0.8);
            border-color: rgba(255, 255, 255, 0.8);
        }

        .Checkbox-content {
            color: rgba(255, 255, 255, 0.9);
        }

        &.Checkbox--hasError .Checkbox-indicator {
            border-color: var(--color-red, #e74c3c);
        }
    }

    &--small {
        margin: 2px 0;

        .Checkbox-input {
            width: 24px;
            height: 24px;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .Checkbox-indicator {
            width: 16px;
            height: 16px;
            position: relative;

            i {
                font-size: 14px;
            }
        }

        .Checkbox-content {
            margin-left: 8px;
            font-size: 12px;
        }
    }

    &--medium {
        .Checkbox-content {
            margin-left: 8px;
            font-size: 12px;
        }
    }

    &--large {
        .Checkbox-input {
            width: 36px;
            height: 36px;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .Checkbox-indicator {
            width: 24px;
            height: 24px;
            position: relative;

            i {
                font-size: 20px;
            }
        }

        .Checkbox-content {
            margin-left: 8px;
            font-size: 16px;
        }
    }
}
</style>
